// Path-based, same-origin in production (nginx proxies /api/* to the
// Hexponents Node service). Same path works in dev once we add a Vite
// proxy or hit prod directly.
const BASE = '/api/v1';

export interface ApiError {
  error: string;
  issues?: unknown;
}

// Fetch wrapper:
// - sends cookies automatically (credentials: 'include')
// - on 401, tries /auth/refresh once and retries the original. The
//   only path that DOESN'T get the refresh treatment is /auth/refresh
//   itself (where 401 means the refresh token is bad and another
//   refresh attempt would loop). Pre-auth endpoints like /auth/login
//   and /auth/signup are technically refreshable, but they don't
//   return 401 on bad credentials (they return 400 with a structured
//   error), so a spurious refresh attempt is harmless.
//
// Earlier versions excluded all `/auth/*` paths — that broke /auth/me
// hydration: every page reload after the 15-min access cookie expired
// looked logged-out, even though the 30-day refresh cookie was still
// valid. The fix is to allow refresh-on-401 for /auth/me specifically;
// the narrower exclusion of just /auth/refresh covers the loop case.
export async function apiFetch(
  path: string,
  init: RequestInit = {},
  _allowRefresh = true,
): Promise<Response> {
  const headers = new Headers(init.headers);
  if (init.body && !headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }

  const res = await fetch(BASE + path, {
    ...init,
    credentials: 'include',
    headers,
  });

  if (
    _allowRefresh &&
    res.status === 401 &&
    path !== '/auth/refresh'
  ) {
    const r = await fetch(BASE + '/auth/refresh', {
      method: 'POST',
      credentials: 'include',
    });
    if (r.ok) {
      return apiFetch(path, init, false);
    }
  }

  return res;
}

export async function apiJson<T>(path: string, init?: RequestInit): Promise<T | null> {
  const res = await apiFetch(path, init);
  if (!res.ok) return null;
  return (await res.json()) as T;
}

// ─────────────────────────────────────────────────────────────────────
// Cache-first puzzle fetch
// ─────────────────────────────────────────────────────────────────────
//
// `/puzzles/:date/:difficulty` is the canonical wire shape — same JSON
// the iOS app fetches. We mirror its read-through caching: check the
// pinned IndexedDB tier first; on hit return the cached Puzzle, on
// miss fall through to the network and (best-effort) populate the
// tier so subsequent reads are local.
//
// Today's daily path on web doesn't currently go through this helper
// (it generates locally via `dailyPuzzle()`), but the archive
// downloader uses it via `apiFetch` directly, and any future
// API-backed call site will pick the cache up "for free".
import type { Difficulty, Puzzle } from '../types';
import { readPinned, writePinned } from './offlineCache';

export async function fetchPuzzle(
  date: string,
  difficulty: Difficulty,
): Promise<Puzzle | null> {
  // Cache-first. Returning a Puzzle from the pinned tier means the
  // user can re-open an archive day offline (planes, subways) as long
  // as they pre-downloaded it.
  const hit = await readPinned(date, difficulty);
  if (hit) return hit;

  const res = await apiFetch(`/puzzles/${date}/${difficulty}`);
  if (!res.ok) return null;
  const puzzle = (await res.json()) as Puzzle;

  // Best-effort backfill so subsequent reads are local. Silently
  // ignore write failures — IDB might be unavailable in private mode
  // and the puzzle is still usable from the response.
  writePinned(date, difficulty, puzzle).catch(() => { /* ignore */ });

  return puzzle;
}
